<template>
    <div class="flex h-full">
        <dx-tree-list
            ref="treeListRef"
            :allow-column-resizing="true"
            :auto-expand-all="true"
            :column-chooser="{ enabled: true, mode: 'select' }"
            :column-min-width="70"
            :columns="meta.columns"
            :data-source="dataSource"
            :filter-panel="{ visible: true }"
            :filter-row="{ visible: true }"
            :focused-row-enabled="true"
            :header-filter="{ visible: true }"
            :hover-state-enabled="true"
            :remote-operations="false"
            :show-borders="true"
            :show-column-lines="true"
            :show-row-lines="true"
            column-resizing-mode="widget"
            filter-mode="fullBranch"
            key-expr="id"
            parent-id-expr="kod_parent"
            @cellClick="onCellClick($event)"
            @toolbar-preparing="onToolbarPreparing($event)"
            @content-ready="onContentReady($event)"
            @focused-row-changed="onFocusedRowChanged($event)"
        >
            <dx-load-panel :enabled="true" />
            <dx-scrolling mode="virtual" />
            <dx-sorting mode="single" />
            <dx-selection mode="multiple" show-check-boxes-mode="always" />
            <dx-editing
                :allow-adding="false"
                :allow-deleting="true"
                :allow-updating="true"
                mode="batch"
                start-edit-action="click"
            />
        </dx-tree-list>

        <div class="flex flex-col h-full gap-2 grid-border max-w-sm">
            <div class="flex flex-col h-0 grow gridContainer">
                <dx-data-grid
                    ref="gridRef"
                    :columns="gridMeta?.columns"
                    :data-source="gridDataSource"
                    :fixing="{ enabled: true }"
                    :header-filter="{ visible: true }"
                    :hover-state-enabled="true"
                    :load-panel="{ enabled: true }"
                    :selection="{
                        mode: 'multiple',
                        showCheckBoxesMode: 'always',
                        allowSelectAll: meta?.allowSelectAll || false,
                    }"
                    :show-borders="true"
                    :show-column-lines="true"
                    :show-row-lines="true"
                    :toolbar="{ items: gridToolbarButtons }"
                    height="100%"
                    @row-inserted="onGridOptionChanged($event)"
                    @row-updated="onGridOptionChanged($event)"
                >
                    <dx-editing
                        :allow-adding="true"
                        :allow-deleting="false"
                        :allow-updating="true"
                        mode="cell"
                        start-edit-action="click"
                    />
                </dx-data-grid>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { DxDataGrid, DxEditing } from 'devextreme-vue/data-grid';
import { DxLoadPanel, DxScrolling, DxSelection, DxSorting, DxTreeList } from 'devextreme-vue/tree-list';
import { getGridMeta, meta } from '@/views/mped/services/mped-nvv-service';
import { getNvvTariff, setNvvTariff } from '@/views/mped/services/mped-nvv-api-service';
import { getAiDataSource } from '@/components/utils/ai-data-source-generator';
import { checkHasEditData, getAiToolbar, undoEditData } from '@/components/utils/ai-toolbar-generator';

const treeListRef = ref();
const gridDataSource = ref([]);
const gridRef = ref();
const FIELD_TYPES = [
    'tech_water',
    'potable_water',
    'water_disposal',
    'total',
];
const currentType = ref();
const gridMeta = computed(() => getGridMeta(currentType.value));
const deleteButton = {
    name: 'deleteButton',
    widget: 'dxButton',
    location: 'after',
    options: {
        hint: 'Удалить',
        icon: 'trash',
        onClick: () => onDeleteClick(),
    },
};
const addButton = {
    name: 'addRowButton',
};
const gridToolbarButtons = ref([addButton, deleteButton]);
let selectedNode;
let toolbarButtons = {};
let selectedRowKey;

const dataSource = getAiDataSource({
    dataFn: getNvvTariff,
    key: 'id',
    insertFn: (values) => {
        return setNvvTariff({
            insertDataArgs: [values],
        });
    },

    updateFn: (id, values) => {
        return setNvvTariff({
            updateDataArgs: [{ id, ...values }],
        });
    },

    removeFn: (id) => {
        return setNvvTariff({
            deleteDataArgs: [{ id }],
        });
    },
});

const onToolbarPreparing = (e) => {
    toolbarButtons = getAiToolbar(e, {
        addRoot: {
            event: () => {
                treeListRef.value.instance.addRow();
            },
        },
        addChild: {
            event: () => {
                if (selectedNode) {
                    treeListRef.value.instance.addRow(selectedNode);
                }
            },
        },
        refresh: {
            event: () => {
                reloadDataAndClearSort();
            },
        },
        save: {
            event: () => {
                treeListRef.value.instance.saveEditData();
            },
        },
        undo: {
            event: () => {
                undoEditData(treeListRef.value.instance);
            },
        },
        expand: {
            event: () => {
                expandAllTree();
            },
        },
    });
};

const onFocusedRowChanged = (e) => {
    selectedNode = e.row.key;
};

const expandAllTree = () => {
    treeListRef.value.instance.getVisibleRows()
        .filter(row => row.node.hasChildren)
        .forEach(row => treeListRef.value.instance.expandRow(row.key));
};

const reloadDataAndClearSort = () => {
    reloadData();
    treeListRef.value.instance.clearSorting();
    treeListRef.value.instance.cancelEditData();
};

const reloadData = () => {
    dataSource.reload();
};

const onCellClick = event => {
    const currentField = event.column?.dataField;
    const rowData = event.row?.data;

    if (!currentField || !rowData) return;

    const isDisabled = rowData.final_value === true && currentField === 'total';
    gridToolbarButtons.value.forEach(button => button.disabled = isDisabled);

    if (FIELD_TYPES.includes(currentField)) {
        selectedRowKey = event.row.key;
        currentType.value = currentField;
        gridDataSource.value = (event.value || []).map(item => ({ [currentField]: item }));
    }
    gridRef.value.instance.option('editing.changes', []);
};

const onContentReady = () => {
    checkHasEditData(treeListRef.value.instance, toolbarButtons);
};

const onGridOptionChanged = () => {
    updateTreeListChanges(
        gridDataSource.value.map(item => item[currentType.value]),
    );
};

const updateTreeListChanges = (restDataSource) => {
    const treeListInstance = treeListRef.value.instance;
    const rowIndex = treeListInstance.getRowIndexByKey(selectedRowKey);
    treeListInstance.cellValue(rowIndex, currentType.value, restDataSource);
    treeListRef.value.instance.refresh();
};

const onDeleteClick = () => {
    const selectedKeys = gridRef.value.instance.getSelectedRowKeys();

    gridDataSource.value = gridDataSource.value.filter(
        item => !selectedKeys.includes(item),
    );

    updateTreeListChanges(gridDataSource.value.map(item => item[currentType.value]));
    treeListRef.value.instance.refresh();
};
</script>

<style scoped>
:deep(.dx-radiogroup-horizontal .dx-collection) {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.grid-border {
    border: 1px solid #e5e7eb;
    width: auto;
}

.gridContainer {
    width: 100%;
    overflow-x: auto;
}
</style>
